import { Injectable, inject } from '@angular/core';
import { PdfService } from './pdf.service';

import JsBarcode from 'jsbarcode';

export interface SpecimenContainerLabelPrintData {
  patientPrimaryId: string;
  patientName?: string;
  patientFallbackId?: string;
  serviceRequestId?: string;
  serviceRequestName?: string;
  specimenType?: string;
  receivedTime?: string;
  containerId?: string;
}

@Injectable({ providedIn: 'root' })
export class SpecimenContainerLabelPrintService {
  private readonly pdf = inject(PdfService);

  private mmToPt(mm: number): number {
    // 1 inch = 25.4mm, 1 point = 1/72 inch
    return (mm / 25.4) * 72;
  }

  private escapeHtml(input: unknown): string {
    const s = (input ?? '').toString();
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private buildCode39Svg(valueRaw: string): string | null {
    const value = (valueRaw ?? '').toString().trim();
    if (!value) return null;

    try {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      // pdfmake's svg renderer is picky; ensure namespace attributes are present.
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

      // Code39 is widely supported by scanners and does not require check digits.
      // We hide the human-readable text because we already print the container id.
      JsBarcode(svg, value.toUpperCase(), {
        format: 'CODE39',
        displayValue: false,
        height: 36,
        margin: 0,
        width: 1.2
      });

      return new XMLSerializer().serializeToString(svg);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate Code39 barcode SVG', e);
      return null;
    }
  }

  printSmallLabel(data: SpecimenContainerLabelPrintData): void {
    // Generate a small PDF label (58mm x 35mm) and print it.
    // This uses the same pdfmake pipeline as the rest of the app.
    const patientPrimaryId = (data.patientPrimaryId ?? 'N/A').toString();
    const patientName = (data.patientName ?? '').toString();
    const patientFallbackId = (data.patientFallbackId ?? '').toString();
    const serviceRequestId = (data.serviceRequestId ?? '').toString();
    const serviceRequestName = (data.serviceRequestName ?? '').toString();
    const specimenType = (data.specimenType ?? '').toString();
    const receivedTime = (data.receivedTime ?? '').toString();
    const containerIdRaw = (data.containerId ?? '').toString();

    const serviceRequestLine = (serviceRequestName || serviceRequestId).toString();
    const receivedLine = receivedTime || 'N/A';
    const containerLine = containerIdRaw || 'N/A';

    const barcodeSvg = containerIdRaw ? this.buildCode39Svg(containerIdRaw) : null;

    const margin = this.mmToPt(2);
    const columnGap = this.mmToPt(1.6);

    const content: any[] = [
      { text: (patientName || patientPrimaryId || 'N/A').toString(), style: 'headline' },
      {
        table: {
          widths: [this.mmToPt(14), '*'],
          body: [
            ['Patient ID', patientPrimaryId || 'N/A'],
            ...(patientFallbackId ? [['Alt ID', patientFallbackId]] : []),
            ...(serviceRequestLine ? [['Request', serviceRequestLine]] : []),
            ...(specimenType ? [['Specimen', specimenType]] : []),
          ]
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 1
        },
        style: 'kvTable',
        margin: [0, 1, 0, 1]
      },
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: 'Received', style: 'k' },
              { text: receivedLine, style: 'v' }
            ]
          },
          {
            width: '*',
            alignment: 'right',
            stack: [
              { text: 'Container', style: 'k', alignment: 'right' },
              { text: containerLine, style: 'v', alignment: 'right' }
            ]
          }
        ],
        columnGap,
        margin: [0, 0, 0, 0]
      },
      ...(barcodeSvg
        ? [{ svg: barcodeSvg, width: this.mmToPt(54), alignment: 'center', margin: [0, 1, 0, 0] }]
        : [])
    ];

    const docDefinition: any = {
      pageSize: {
        width: this.mmToPt(58),
        height: this.mmToPt(35)
      },
      pageMargins: [margin, margin, margin, margin],
      content,
      styles: {
        headline: { fontSize: 10.5, bold: true },
        k: { fontSize: 6, color: '#666' },
        v: { fontSize: 8, bold: true },
        muted: { fontSize: 7, color: '#666' },
        kvTable: { fontSize: 6.8 }
      },
      defaultStyle: { fontSize: 8 }
    };

    this.pdf.print(docDefinition);
  }
}
